import React, { useEffect, useState } from 'react'
import { CirclePlus } from 'lucide-react'

import { cn } from '../../../lib/utils'

import customImageComponent from '../../../components/common/CustomImageComponent'
import { IconArrowCircle } from '../../../components/icons/IconArrowCircle'
import { MissingTeamMember } from '../../../components/modals/InfoModals/components/tabs/PaperworkTab/components/MissingTeamMember/MissingTeamMember'
import { AccordionWrapper } from './AccordionWrapper'

import { calculateAge } from '../../../models/interface.helper'
import { sortByRiderRole } from '../../../helpers/registration-on-event'
import { haveTeamMember, isValidTeamMember } from '../../../helpers/teamMembers'

import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../../../models/registrations-by-day/registrationByDay.interface'

import { IPaperworkTeam } from '../../../models/user-documents/user-documents.interface'
import { IEventReviewPublish } from '../../../models/event-review-publish/event-review-publish.interface'

import { CONST } from '../../../const/const'

interface IPropsHorseAccordion {
  type?: 'overview' | 'registrar'
  teamMembers: IPaperworkTeam[]
  item: IRegistrationByDayInterface
  addSignerHandler: (
    defaultTeamMemberId: string | null,
    defaultRole: string | null,
    onlyPaperwork?: boolean
  ) => void
  eventReviewPublish: IEventReviewPublish | null
}

export const HorseAccordionWrapper = ({
  type,
  teamMembers,
  item,
  addSignerHandler,
  eventReviewPublish,
}: IPropsHorseAccordion) => {
  const [isOpen, setIsOpen] = useState(false)
  const [signMissing, setSignMissing] = useState(0)
  const [isMissedGuardian, setIsMissedGuardian] = useState(false)
  const [uniqueRoles, setUniqueRoles] = useState<string[]>([])
  const [openedMenu, setOpenedMenu] = useState<number[]>([])

  useEffect(() => {
    let missedGuardian = false
    const roles: string[] = []

    teamMembers.forEach((member) => {
      const age = calculateAge(member?.teamMemberDob)
      let isRider = member.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.RIDER
      const guardian = teamMembers.find(
        (teamMember) => teamMember.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.GUARDIAN
      )
      if (age < 18 && isRider && !guardian) {
        missedGuardian = true
      }

      if (member?.teamMemberRole && !roles.includes(member?.teamMemberRole)) {
        roles.push(member.teamMemberRole)
      }
    })

    setSignMissing(isValidTeamMember(teamMembers))
    setUniqueRoles(roles)
    setIsMissedGuardian(missedGuardian)
  }, [eventReviewPublish?.EventPaperwork, teamMembers])

  const handleToggle = (value: number) => {
    const exist = openedMenu.includes(value)

    if (exist) {
      setOpenedMenu(() => {
        return openedMenu.filter((current) => {
          return current !== value
        })
      })
    } else {
      setOpenedMenu((prevState) => [...prevState, value])
    }
  }

  const haveRider = haveTeamMember(teamMembers, CONST.UI.TEAM_MEMBERS_ROLES.RIDER)
  const haveTrainer = haveTeamMember(teamMembers, CONST.UI.TEAM_MEMBERS_ROLES.TRAINER)
  const haveOwner = haveTeamMember(teamMembers, CONST.UI.TEAM_MEMBERS_ROLES.OWNER)

  return (
    <div
      className={cn(
        'py-4 text-SeabiscuitDark200ThemeColor',
        type === 'overview' || type === 'registrar'
          ? '[&:not(:last-child)]:border-b-black/10 [&:not(:last-child)]:border-b'
          : 'px-7 mt-5 border-black/10 border rounded-lg',
        type === 'overview' && 'pointer-events-none'
      )}
    >
      <div
        onClick={() => type !== 'overview' && setIsOpen(!isOpen)}
        className="flex flex-col lg:flex-row lg:justify-between lg:items-center font-semibold accordionParent cursor-pointer"
      >
        <div className="flex items-center">
          <span className="w-[45px] h-[45px] rounded-md overflow-hidden mr-4 border border-black/10">
            {customImageComponent(
              item.horseProfilePicture,
              item.horseName,
              'w-full h-full object-cover'
            )}
          </span>
          <div className="flex flex-col">
            <span className="text-md text-SeabiscuitDark200ThemeColor font-semibold capitalize">
              {item.horseName || HORSE_NAME_DEFAULT}
              <div className="font-normal text-[12px] text-[#3C4B71B2]">
                {teamMembers ? teamMembers.length : 1} team member
                {teamMembers && teamMembers.length > 1 && 's'} •{' '}
                {uniqueRoles ? uniqueRoles.length : 1} role
                {(uniqueRoles && uniqueRoles.length > 1) || !uniqueRoles ? 's' : ''}
              </div>
            </span>
          </div>
        </div>
        <div className="flex justify-between mt-3 lg:mt-0 items-center">
          <div
            className={cn(
              'font-normal text-[14px]  rounded-full py-0.5 px-3',
              isMissedGuardian || signMissing || !haveRider
                ? 'text-SeabiscuitRedThemeColor bg-SeabiscuitRedThemeColor/5'
                : 'text-black/50 bg-black/5'
            )}
          >
            {isMissedGuardian
              ? 'Guardian missing'
              : !haveRider
                ? 'Rider signature missing'
                : signMissing === 0 && haveRider
                  ? 'All signed'
                  : `${signMissing} ${signMissing === 1 ? 'signature' : 'signatures'} missing`}
          </div>
          {type !== 'overview' && (
            <div className="ml-2 status rounded-full cursor-pointer w-[30px] h-[30px] flex items-center justify-center">
              <IconArrowCircle
                className={cn('w-full', isOpen && 'rotate-180')}
                color={isMissedGuardian || signMissing || !haveRider ? '#E80000' : '#000000'}
              />
            </div>
          )}
        </div>
      </div>
      <div className={cn('faqs-content mt-5', isOpen && 'activeTab')}>
        {type !== 'overview' && !haveRider && (
          <MissingTeamMember
            name={item.riderName}
            profilePicture={item.riderProfilePicture}
            role={item.riderName || 'Rider'}
            description={`Rider, Signature missing`}
            onEdit={() => addSignerHandler(item.riderId, CONST.UI.TEAM_MEMBERS_ROLES.RIDER, true)}
          />
        )}
        {sortByRiderRole(teamMembers)?.map((paperworkMember, teamMemberIndex) => (
          <AccordionWrapper
            key={teamMemberIndex}
            type={type}
            openedMenu={openedMenu}
            handleToggle={handleToggle}
            teamMemberIndex={teamMemberIndex}
            userData={paperworkMember as IPaperworkTeam}
            eventReviewPublish={eventReviewPublish}
          />
        ))}
        {type !== 'overview' && (
          <>
            {!haveTrainer && (
              <MissingTeamMember
                role="Trainer"
                description="Missing"
                onEdit={() => addSignerHandler(null, CONST.UI.TEAM_MEMBERS_ROLES.TRAINER)}
              />
            )}
            {!haveOwner && (
              <MissingTeamMember
                role="Owner"
                description="Missing"
                onEdit={() => addSignerHandler(null, CONST.UI.TEAM_MEMBERS_ROLES.OWNER)}
              />
            )}
          </>
        )}
        <div className="pt-3 border-black/10 border-t">
          <button
            className="flex gap-2 items-center justify-center p-3 w-full bg-[#F1F1F1] text-sm text-black/50 rounded-lg hover:bg-SeabiscuitMainThemeColor hover:text-white transition-all"
            onClick={() => addSignerHandler(null, null)}
          >
            <CirclePlus className="w-4" />
            Add signer
          </button>
        </div>
      </div>
    </div>
  )
}
